import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Select from '../../../../../../components/forms/Select/Select';
import SimpleLoader from '../../../../../../components/Loader/SimpleLoader';
import { patchFormContentShowIf } from '../../../../../../services/forms/formContentService';
import { CHOICE_TYPES, plainText } from '../../../../formUtils';
import { useFormEdit } from '../../FormEditContext';

const questionLabel = (contents, question) => {
  const text = plainText(question.name).trim();
  const short = text.length > 70 ? `${text.slice(0, 70)}…` : text;
  return `${contents.indexOf(question) + 1}. ${short}`;
};

/**
 * Условие показа (content.show_if): вопрос виден респонденту, только если
 * в вопросе выше выбран хотя бы один из отмеченных вариантов. Невыбранная
 * карточка показывает условие строкой, выбранная — редактор условия.
 * open — редактор открыт (из меню «⋮» или потому что условие уже есть):
 * он не пропадает, пока снимают и ставят варианты, закрывает его крестик.
 */
export default function ContentCondition({
  content,
  contents,
  selected,
  setContents,
  open,
  setOpen,
}) {
  const { reportError } = useFormEdit();
  const [saving, setSaving] = useState(false);
  // вопрос выбран, а вариантов ещё не отметили — сохранять пока нечего
  const [pickedUUID, setPickedUUID] = useState(null);

  const showIf = content.show_if ?? [];
  const parent =
    contents.find((c) =>
      c.questionselectcontent?.some((o) => showIf.includes(o.uuid)),
    ) ?? contents.find((c) => c.uuid === pickedUUID);

  if (!showIf.length && !open) return null;

  const index = contents.findIndex((c) => c.uuid === content.uuid);
  const candidates = contents
    .slice(0, index)
    .filter(
      (c) =>
        CHOICE_TYPES.includes(c.test_type) && c.questionselectcontent?.length,
    );
  // после перетаскивания вопрос условия может оказаться ниже
  if (parent && !candidates.includes(parent)) candidates.push(parent);

  const save = (options) => {
    setSaving(true);
    return patchFormContentShowIf(content.uuid, options)
      ?.then((res) =>
        setContents((prev) =>
          prev.map((c) =>
            c.uuid === content.uuid ? { ...c, show_if: res.show_if } : c,
          ),
        ),
      )
      ?.catch((err) => reportError(err, 'Не удалось сохранить условие'))
      ?.finally(() => setSaving(false));
  };

  const parentOptions = parent?.questionselectcontent ?? [];
  const chosenLabels = parentOptions
    .filter((o) => showIf.includes(o.uuid))
    .map((o) => o.content);

  let warning;
  if (parent && !CHOICE_TYPES.includes(parent.test_type)) {
    warning =
      'У вопроса условия больше нет вариантов ответа — этот вопрос никто не увидит.';
  } else if (parent && contents.indexOf(parent) > index) {
    warning = 'Вопрос условия стоит ниже этого вопроса.';
  }

  if (!selected) {
    if (!parent || !chosenLabels.length) return null;
    return (
      <div className='ml-6 flex flex-row items-start gap-2 text-sm text-slate-500'>
        <EyeIcon className='mt-0.5 h-4 min-w-fit' />
        <span>
          Показывается, если в «{questionLabel(contents, parent)}» выбрано:{' '}
          {chosenLabels.join(', ')}
        </span>
      </div>
    );
  }

  return (
    <div className='ml-6 flex flex-col gap-3 rounded-md bg-slate-50 p-3 text-sm'>
      <div className='flex flex-row items-center gap-2'>
        <EyeIcon className='h-4 min-w-fit text-slate-500' />
        <span className='min-w-fit'>Показывать, только если в вопросе</span>
        <Select
          className='w-full'
          placeholder='Выберите вопрос'
          isDisabled={saving}
          value={parent?.uuid}
          options={candidates.map((c) => ({
            label: questionLabel(contents, c),
            value: c.uuid,
          }))}
          onChange={(e) => {
            if (e?.value === parent?.uuid) return;
            setPickedUUID(e?.value);
            // варианты прежнего вопроса к новому не относятся
            if (showIf.length) save([]);
          }}
        />
        {saving ? (
          <div className='relative'>
            <SimpleLoader className='h-5' />
          </div>
        ) : (
          <XMarkIcon
            className='h-8 min-w-fit cursor-pointer rounded-full p-1.5 hover:bg-slate-200'
            title='Убрать условие'
            onClick={() => {
              setPickedUUID(null);
              setOpen(false);
              if (showIf.length) save([]);
            }}
          />
        )}
      </div>
      {!candidates.length && (
        <span className='text-slate-500'>
          Выше нет вопросов с вариантами ответа — условие поставить не на что.
        </span>
      )}
      {parent && (
        <div className='flex flex-col gap-2'>
          <span>выбран любой из вариантов:</span>
          {parentOptions.map((option) => (
            <label
              key={option.uuid}
              className='flex cursor-pointer flex-row items-center gap-2'
            >
              <input
                type='checkbox'
                className='h-4 w-4 cursor-pointer rounded'
                disabled={saving}
                checked={showIf.includes(option.uuid)}
                onChange={(e) => {
                  // сняли последний вариант — вопрос условия остаётся выбранным
                  setPickedUUID(parent.uuid);
                  save(
                    e.target.checked
                      ? [...showIf, option.uuid]
                      : showIf.filter((uuid) => uuid !== option.uuid),
                  );
                }}
              />
              {option.content}
            </label>
          ))}
          {!!parentOptions.length && !showIf.length && (
            <span className='text-slate-500'>
              Пока ни один вариант не отмечен, вопрос показывается всегда.
            </span>
          )}
        </div>
      )}
      {warning && (
        <span className='flex flex-row items-center gap-2 text-amber-700'>
          <ExclamationTriangleIcon className='h-4 min-w-fit' />
          {warning}
        </span>
      )}
    </div>
  );
}
